/**
 * Local feature-flag overrides — a PostHog-independent escape hatch so QA can
 * exercise flag-gated UI (Content Suite, etc.) on environments where PostHog is
 * disabled (feature envs ship with an empty key, so every flag fails closed).
 *
 * A flag set here wins over PostHog. Seed it from the URL once — `?ff=a,b` turns
 * flags on, `?ff_off=c` off, `?ff_clear=1` resets — and it persists in
 * localStorage across in-app navigation. Restricted to local dev and
 * `*.hapana-feature.com` so real production is never affected.
 */
export const FF_OVERRIDE_STORAGE_KEY = 'hapana:ff-overrides'

const overridesAllowed = (): boolean => {
  if (typeof window === 'undefined') {
    return false
  }
  const host = window.location.hostname
  return (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host.endsWith('.localhost') ||
    host.endsWith('.hapana-feature.com')
  )
}

const readFlagOverrides = (): Record<string, boolean> => {
  if (!overridesAllowed()) {
    return {}
  }
  try {
    const raw = window.localStorage.getItem(FF_OVERRIDE_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as Record<string, boolean>) : {}
  } catch {
    return {}
  }
}

const writeFlagOverrides = (map: Record<string, boolean>): void => {
  try {
    window.localStorage.setItem(FF_OVERRIDE_STORAGE_KEY, JSON.stringify(map))
  } catch {
    // localStorage unavailable (private mode / disabled) — overrides just don't persist.
  }
}

export const syncFlagOverridesFromUrl = (): void => {
  if (!overridesAllowed()) {
    return
  }
  const params = new URLSearchParams(window.location.search)
  if (params.get('ff_clear')) {
    writeFlagOverrides({})
    return
  }
  const on = params.get('ff')
  const off = params.get('ff_off')
  if (!on && !off) {
    return
  }
  const map = readFlagOverrides()
  const apply = (csv: string | null, value: boolean): void =>
    csv
      ?.split(',')
      .map((s) => s.trim())
      .filter(Boolean)
      .forEach((key) => {
        map[key] = value
      })
  apply(on, true)
  apply(off, false)
  writeFlagOverrides(map)
}

export const getFlagOverride = (flagKey: string): boolean | undefined => {
  const map = readFlagOverrides()
  return Object.prototype.hasOwnProperty.call(map, flagKey) ? map[flagKey] : undefined
}
